import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Car } from 'lucide-react';

const API = window.location.origin;

export default function ChatPanel({ location, liked, passed, onVehiclesUpdate }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: location.city
        ? `Salut ! J'ai trouvé des véhicules à moins de ${location.radius} km de ${location.city}. Qu'est-ce que vous cherchez ?`
        : 'Salut ! Dites-moi ce que vous cherchez, ou swipez ce qui vous plaît !',
      quick_replies: ['SUV', 'Berline', 'Break', 'Budget < 15k'],
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (location.city && messages.length === 1) {
      setMessages([{
        role: 'assistant',
        content: `Salut ! J'ai trouvé des véhicules à moins de ${location.radius} km de ${location.city}. Qu'est-ce que vous cherchez ?`,
        quick_replies: ['SUV', 'Berline', 'Break', 'Budget < 15k'],
      }]);
    }
  }, [location.city]);

  const send = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages.slice(-18).map(m => ({ role: m.role, content: m.content })),
          liked_ids: liked,
          passed_ids: passed,
          geo: location,
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply,
        quick_replies: data.quick_replies || [],
      }]);
      if (data.vehicles?.length) onVehiclesUpdate(data.vehicles);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Désolé, une erreur est survenue. Réessayez.',
        quick_replies: ['Réessayer'],
      }]);
    } finally {
      setLoading(false);
    }
  };

  const lastAssistantIdx = messages.reduce((acc, m, i) => m.role === 'assistant' ? i : acc, -1);

  return (
    <div className="flex flex-col h-full bg-dark-bg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full border-2 border-carmin/60 flex items-center justify-center">
            <Car size={13} className="text-carmin" />
          </div>
          <span className="text-xs text-muted font-medium tracking-wide">Conseiller Carmin</span>
        </div>
      </div>

      {/* Messages — alternating: AI left plain, User right grey tag */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 pt-3 pb-2 scrollbar-hide">
        {messages.map((msg, i) => (
          <div key={i} className={`mb-5 ${msg.role === 'user' ? 'flex justify-end' : ''}`}>
            {msg.role === 'user' ? (
              /* User message — grey rounded tag, aligned right */
              <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
                <p className="text-[14px] leading-relaxed text-secondary">
                  {msg.content}
                </p>
              </div>
            ) : (
              /* AI message — plain text, full width, aligned left */
              <div>
                <p className="text-[16px] leading-[1.7] text-primary/90 font-light tracking-[-0.01em]">
                  {msg.content}
                </p>

                {/* Quick replies — white pills */}
                {msg.quick_replies?.length > 0 && i === lastAssistantIdx && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {msg.quick_replies.map((qr, j) => (
                      <button
                        key={j}
                        onClick={() => send(qr)}
                        disabled={loading}
                        className="px-4 py-2 rounded-full bg-white text-dark-bg text-[13px] font-medium hover:bg-white/85 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {qr}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="mb-5 flex items-center gap-2.5 text-muted">
            <Loader2 size={15} className="animate-spin" />
            <span className="text-[14px]">Recherche en cours...</span>
          </div>
        )}
      </div>

      {/* Input bar — dark rounded pill */}
      <div className="px-4 pb-4 pt-2">
        <div className="flex items-center gap-2 bg-dark-card border border-dark-border rounded-full px-4 py-1">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(input); }}
            placeholder="Posez vos questions à Carmin..."
            disabled={loading}
            className="flex-1 bg-transparent text-[14px] text-primary placeholder-muted/50 outline-none py-2.5 disabled:opacity-50"
          />
          <button
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            className="w-8 h-8 rounded-full flex items-center justify-center text-muted hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
